import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';

type AccountStatus = {
  charges_enabled: boolean;
  payouts_enabled: boolean;
  requirements_currently_due: string[];
};

export default function PayoutsReturn() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAccountStatus = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          throw new Error('Not authenticated');
        }

        // Call connect-status endpoint to get fresh data from Stripe
        // This also updates the profile with the latest status
        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;

        if (!baseUrl) {
          throw new Error('Configuration error');
        }

        const response = await fetch(`${baseUrl}/functions/v1/connect-status`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to check account status');
        }

        const data = await response.json();

        if (!data.connected) {
          // No Stripe account connected yet
          setStatus({
            charges_enabled: false,
            payouts_enabled: false,
            requirements_currently_due: [],
          });
        } else {
          setStatus({
            charges_enabled: data.status?.charges_enabled ?? false,
            payouts_enabled: data.status?.payouts_enabled ?? false,
            requirements_currently_due: data.requirements?.currently_due ?? [],
          });
        }
      } catch (err) {
        console.error('Error checking account status:', err);
        setError(err instanceof Error ? err.message : 'Failed to check account status');
      } finally {
        setLoading(false);
      }
    };

    checkAccountStatus();
  }, []);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleCompleteSetup = async () => {
    try {
      setLoading(true);
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (!baseUrl) {
        throw new Error('Configuration error');
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resume setup');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error resuming setup:', err);
      setError(err instanceof Error ? err.message : 'Failed to resume setup');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FEF6D6]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C2731] mx-auto mb-4"></div>
          <p className="text-[#2C2731]">Checking your account status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FEF6D6] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#2C2731] p-8 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <XCircleIcon className="h-16 w-16 text-red-600 mb-4" />
            <h1 className="text-2xl font-semibold text-[#2C2731] mb-2">Something went wrong</h1>
            <p className="text-[#2C2731]/70 mb-6">{error}</p>
            <button
              onClick={handleContinue}
              className="px-5 py-2.5 rounded-md bg-[#2C2731] text-[#FEF6D6] hover:bg-[#241F2A] transition-colors text-sm font-medium"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isFullySetup = status?.payouts_enabled && status?.charges_enabled;
  const hasRequirements = status?.requirements_currently_due && status.requirements_currently_due.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FEF6D6] p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#2C2731] p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          {isFullySetup ? (
            <>
              <CheckCircleIcon className="h-16 w-16 text-green-600 mb-4" />
              <h1 className="text-2xl font-semibold text-[#2C2731] mb-2">
                Payments Setup Complete!
              </h1>
              <p className="text-[#2C2731]/70 mb-6">
                Your Stripe Connect account is fully configured. You can now receive payments from seekers.
              </p>
              <div className="w-full bg-[#E8DFBD] rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-medium text-[#2C2731] mb-2">Account Status</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <span className="text-[#2C2731]">Charges enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <span className="text-[#2C2731]">Payouts enabled</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                <span className="text-3xl">⏳</span>
              </div>
              <h1 className="text-2xl font-semibold text-[#2C2731] mb-2">
                Setup In Progress
              </h1>
              <p className="text-[#2C2731]/70 mb-6">
                Your Stripe Connect account is being processed.
                {hasRequirements && ' You may need to complete additional verification steps.'}
              </p>
              <div className="w-full bg-[#E8DFBD] rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-medium text-[#2C2731] mb-2">Account Status</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    {status?.charges_enabled ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-[#2C2731]">Charges {status?.charges_enabled ? 'enabled' : 'pending'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {status?.payouts_enabled ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-[#2C2731]">Payouts {status?.payouts_enabled ? 'enabled' : 'pending'}</span>
                  </div>
                </div>
                {hasRequirements && (
                  <div className="mt-3 pt-3 border-t border-[#2C2731]/20">
                    <p className="text-xs text-[#2C2731]/70">
                      Additional information required: {status.requirements_currently_due.join(', ')}
                    </p>
                  </div>
                )}
              </div>
              {hasRequirements && (
                <button
                  onClick={handleCompleteSetup}
                  className="w-full mb-3 px-5 py-2.5 rounded-md bg-[#2C2731] text-[#FEF6D6] hover:bg-[#241F2A] transition-colors text-sm font-medium"
                >
                  Complete Setup
                </button>
              )}
            </>
          )}
          <button
            onClick={handleContinue}
            className={`px-5 py-2.5 rounded-md transition-colors text-sm font-medium ${
              isFullySetup
                ? 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#241F2A]'
                : 'border border-[#2C2731] text-[#2C2731] hover:bg-[#E8DFBD]'
            }`}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
